import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@config/axios/axios.config';
import { paths } from '@common/types';
import { ISubmissionTableFilters } from '@views/pages/DataAnalysis/components/Submissions/hooks';
import dayjs from 'dayjs';
import { upperCase } from 'lodash';
import { stableKey } from '@common/cache/util.ts';

const programListRequest = async () => {
  const response =
    axiosInstance.get<
      paths[`/program/list`]['get']['responses']['200']['content']['application/json']
    >(`/program/list`);
  const data = (await response).data;

  return data;
};

export const useProgramListQuery = () => {
  const query = useQuery({
    queryKey: ['programList'],
    queryFn: () => programListRequest(),
  });

  return query;
};

const allProgramListRequest = async () => {
  const response =
    axiosInstance.get<
      paths[`/program/list-all`]['get']['responses']['200']['content']['application/json']
    >(`/program/list-all`);
  const data = (await response).data;

  return data;
};

export const useAllProgramListQuery = () => {
  const query = useQuery({
    queryKey: ['allProgramList'],
    queryFn: () => allProgramListRequest(),
  });

  return query;
};

const patientTaskProgramsRequest = async (patient_id: string) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/tasks/programs`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/tasks/programs`);
  const data = (await response).data;

  return data;
};

export const usePatientTaskProgramsQuery = (patient_id: string) => {
  const query = useQuery({
    enabled: !!patient_id,
    queryKey: ['patientTaskPrograms', patient_id],
    queryFn: () => patientTaskProgramsRequest(patient_id),
  });

  return query;
};

interface IProgramStatusesProps {
  program_id: string | undefined;
}

const programStatusesRequest = async ({
  program_id,
}: IProgramStatusesProps) => {
  const response = axiosInstance.get<
    paths[`/program/{program_id}/statuses`]['get']['responses']['201']['content']['application/json']
  >(`/program/${program_id}/statuses`);
  const data = (await response).data;

  return data;
};

export const useProgramStatusesQuery = ({
  program_id,
}: IProgramStatusesProps) => {
  const query = useQuery({
    enabled: !!program_id,
    queryKey: ['program', program_id, 'statuses'],
    queryFn: () => programStatusesRequest({ program_id }),
  });

  return query;
};

interface IProgramTiersProps {
  program_id: string | undefined;
}

const programTiersRequest = async ({ program_id }: IProgramTiersProps) => {
  const response = axiosInstance.get<
    paths[`/program/{program_id}/tiers`]['get']['responses']['201']['content']['application/json']
  >(`/program/${program_id}/tiers`);
  const data = (await response).data;

  return data;
};

export const useProgramTiersQuery = ({ program_id }: IProgramTiersProps) => {
  const query = useQuery({
    enabled: !!program_id,
    queryKey: ['program', program_id, 'tiers'],
    queryFn: () => programTiersRequest({ program_id }),
  });

  return query;
};

interface IProgramStatusHistoryProps {
  patient_id: string;
  program_id: string;
}

const patientProgramStatusHistoryRequest = async (
  props: IProgramStatusHistoryProps,
) => {
  const response = axiosInstance.get<
    paths[`/program/{program_id}/{patient_id}/status-history`]['get']['responses']['200']['content']['application/json']
  >(`/program/${props.program_id}/${props.patient_id}/status-history`);
  const data = (await response).data;

  return data;
};

export const useProgramStatusHistoryQuery = (
  props: IProgramStatusHistoryProps,
) => {
  const query = useQuery({
    queryKey: ['programStatusHistory', props.patient_id, props.program_id],
    enabled: !!props.patient_id && !!props.program_id,
    queryFn: () => patientProgramStatusHistoryRequest(props),
  });

  return query;
};

const welcomeLettersListRequest = async (
  program_id: string,
  record_id: string,
  params: ISubmissionTableFilters,
) => {
  const response = axiosInstance.get<
    paths[`/program/{program_id}/{record_id}/welcome-letter-batches`]['get']['responses']['200']['content']['application/json']
  >(`/program/${program_id}/${record_id}/welcome-letter-batches`, {
    params: {
      ...params,
      sort_order: upperCase(params.sort_order),
      from: dayjs(params.from).format('MMM DD, YYYY'),
      to: dayjs(params.to).format('MMM DD, YYYY'),
    },
  });
  const data = (await response).data;

  return data;
};

export const useWelcomeLettersListQuery = (
  program_id: string,
  record_id: string,
  params: ISubmissionTableFilters,
) => {
  const query = useQuery({
    enabled: !!program_id && !!record_id,
    queryKey: ['welcomeLettersList', program_id, record_id, stableKey(params)],
    queryFn: () => welcomeLettersListRequest(program_id, record_id, params),
  });

  return query;
};

const submissionsListRequest = async (
  program_id: string,
  record_id: string,
  params: ISubmissionTableFilters,
) => {
  const response = axiosInstance.get<
    paths[`/program/{program_id}/{record_id}/submissions`]['get']['responses']['200']['content']['application/json']
  >(`/program/${program_id}/${record_id}/submissions`, {
    params: {
      ...params,
      sort_order: upperCase(params.sort_order),
      from: dayjs(params.from).format('MMM DD, YYYY'),
      to: dayjs(params.to).format('MMM DD, YYYY'),
    },
  });
  const data = (await response).data;

  return data;
};

export const useSubmissionsListQuery = (
  program_id: string,
  record_id: string,
  params: ISubmissionTableFilters,
) => {
  const query = useQuery({
    enabled: !!program_id && !!record_id,
    queryKey: ['submissionsList', program_id, record_id, stableKey(params)],
    queryFn: () => submissionsListRequest(program_id, record_id, params),
  });

  return query;
};
